// @ts-strict-ignore
import {ChartType, FUNNELS_MATH_TYPES, REPORT_BASICS_LINKS, ReportName, SPECIAL_EVENTS} from 'common/report/constants';
import {FilterSection} from 'common/report/insights/models/section';
import {getEntries} from 'common/report/insights/util/events';
import {getPropertyFilters, getPropertyFiltersOperator} from 'common/report/insights/util/filters';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {getDateRange} from 'common/report/insights/util/time';
import {getMultiAttributionProperty, getQueryModeRowTypes, hasOnlyEventBasedMetrics} from 'common/report/insights/util';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {QueryBuilderProps, QueryBuilderUpdateDetail} from 'common/query-builder/query-builder/types';
import {
  QueryMultiMetricEntry,
  QuerySuperEntry,
  RowLabelTheme,
  RowLabelType,
} from 'common/query-builder/query-entry-section/types';
import {ReportQueryBuilder} from 'common/report/report-manager/query-builder';
import {InsightsBookmarkParams, InsightsQueryError, InsightsQueryResults} from 'common/report/insights/util/types';
import {LiftType} from 'common/report/util/lift/types';
import {getFirstSegment, isCohortSegment} from 'common/query-builder/query-block/segment-menu/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {canUseViewN} from 'common/components/data-table/utils';
import {
  BEHAVIOR_AGGREGATION,
  BEHAVIOR_FREQUENCY,
  BEHAVIOR_MULTI_ATTRIBUTION,
  BEHAVIOR_MULTI_ATTRIBUTION_FILTER,
  FUNNELS_TIME_TO_CONVERSION,
  PAGE_DURATION,
  RECURRING_REVENUE_CHANGE,
  RECURRING_REVENUE_CHANGE_TYPE,
} from 'common/query-builder/query-block/properties-menu/constants';
import {
  getSelectedLabelforBehavior,
  isMultiAttributionSegmentation,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {canUseCountUsersOnce, canUseMultiAttributionInReport} from 'common/widgets/multi-attribution-menu/utils';
import {addToast} from 'common/components/toast-area/toast-manager';
import {MetricType} from 'common/query-builder/query-block/constants';
import {getFormulaDisabledReason, getFormulas} from 'common/query-builder/insights-formula/utils';
import {
  hasFunnelMetric,
  hasFunnelTimeToConvertBreakdownBookmark,
  isBehaviorShowClause,
  isEmptyBehaviorShowClause,
} from 'common/report/insights/models/utils';
import {RollingWindowSize} from 'common/util';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {segmentationToPropertyItem} from 'common/query-builder/query-block/properties-menu/util';
import {Segmentation} from 'common/types/reports/query';
import {FeatureRolloutName, FeatureRollouts} from 'common/report/util/feature-sampling';
import {IncludeCustomPropertyType} from 'common/query-builder/query-block/properties-menu/types';

import {INSIGHTS_DEFAULT_EVENT, INSIGHTS_DEFAULT_METRIC, Report} from '../models/report';
import {BehaviorShowClause} from '../models/show-clause';
import {BuilderSections} from '../models/builder-sections';
import {updateReportFromQBSelection} from './qb-update';

type InsightsQueryBuilderState = {
  report: Report | null;
};
class InsightsQueryBuilder extends ReportQueryBuilder<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryBuilderState
> {
  displayContext = SearchDisplayContext.Insights;
  reportType = ReportName.Insights;
  _error: Nullable<InsightsQueryError> = null;

  get defaultState() {
    return {
      report: null,
    };
  }

  bookmarkUpdateFromQbUpdate(updateDetail: QueryBuilderUpdateDetail): InsightsBookmarkParams {
    const oldReport = this.state.report;
    const report = updateReportFromQBSelection({
      selection: updateDetail,
      report: this.state.report,
      customEvents: this.params.customEvents,
      canUseViewN: canUseViewN(),
      isCountUsersOnceEnabled: canUseCountUsersOnce(this.params.project, this.params.insightsReportMode),
      metricsResource: this.getContext(`metrics`),
      behaviorsResource: this.getContext(`behaviors`),
      projectTimezone: this.params.project.timezone ?? null,
      projectAsOfDate: this.params.project.projectAsOfDate,
    });
    // add a toast with cta to change count type to total-conversions + compare to overall + bar chart if ttc breakdown is added
    if (
      hasFunnelTimeToConvertBreakdownBookmark(report.toBookmarkData()) &&
      !hasFunnelTimeToConvertBreakdownBookmark(oldReport.toBookmarkData())
    ) {
      const reportToConfirm = report.clone();
      reportToConfirm.sections.show.clauses.forEach((clause) => {
        if (clause instanceof BehaviorShowClause && clause.behavior.type === MetricType.Funnel) {
          clause.measurement.math = FUNNELS_MATH_TYPES.GENERAL;
          clause.measurement.property = null;
        }
      });
      reportToConfirm.displayOptions.chartType = ChartType.Bar;
      // Time to convert breakdown is kinda ok
      reportToConfirm.liftComparison = {
        type: LiftType.Overall,
      };
      // This update is for confirm all of ChartType + Measurement + LiftType changes
      addToast({
        message: `Show distribution of total conversions by time to convert.`,
        cta: `Apply`,
        timeout: 20000,
        ctaCallback: () => {
          this.dispatchUpdate({
            params: reportToConfirm.toBookmarkData(),
            queryReason: updateDetail.queryReason,
            requery: true,
          });
        },
      });
    }
    return report.toBookmarkData();
  }

  public connectedCallback() {
    super.connectedCallback();
    this.update({
      report: Report.fromBookmarkData(this.params.bookmarkParams),
    });
  }

  qbChildrenTemplate() {
    return null;
  }

  reportSpecificQBParams(): Partial<QueryBuilderProps> {
    if (!this.state.report) {
      return {};
    }

    const {displayOptions, sections} = this.state.report;
    const bookmarkParams = this.state.report.toBookmarkData();
    const showSection = bookmarkParams.sections.show;
    let entries = getEntries({
      showClauses: showSection,
      behaviorsResource: this.getContext(`behaviors`),
      dataGroups: this.params.dataGroups,
      queryError: this.params.error,
      dateUnit: sections.time.clauses[0].unit,
      reportSectionsMetadata: this.params.results?.meta?.report_sections,
    });
    const dataGroupId = sections.getDataGroupId();

    const groupBy = getSelectedSegments(sections.group.clauses);
    const groupByUndraggableRows = groupBy.reduce((acc, segment, idx) => {
      if (isCohortSegment(segment) || isMultiAttributionSegmentation(getFirstSegment(segment))) {
        acc.push(idx);
      }
      return acc;
    }, [] as number[]);

    const reportName =
      this.params.insightsReportMode === BookmarkType.Funnels
        ? ReportName.Funnels
        : this.params.insightsReportMode === BookmarkType.Retention
          ? ReportName.Retention
          : ReportName.Insights;

    const isNewMetricMenuEnabled = FeatureRollouts[FeatureRolloutName.NewMetricMenu].isEnabled(this.params.project);

    if (
      isNewMetricMenuEnabled &&
      reportName === ReportName.Insights &&
      showSection.length === 1 &&
      isBehaviorShowClause(showSection[0]) &&
      isEmptyBehaviorShowClause(showSection[0])
    ) {
      entries = [];
    }

    const canUseMultiAttributionVal = canUseMultiAttributionInReport(this.params.project, reportName);
    const multiAttributionProperty = getMultiAttributionProperty(sections.group.clauses);
    const hasMultiAttributionProp = Boolean(multiAttributionProperty);
    const formulasDisableReason = getFormulaDisabledReason(this.state.report);
    return {
      allowCohortFilter: true,
      disableAddEntry: [BookmarkType.Funnels, BookmarkType.Retention].includes(this.params.insightsReportMode),
      addEntryButtonLabel: this.t(`query-builder.add-metric`),
      addEntryButtonTooltipText: this.t(`query-builder.add-metric-tooltip`, {
        url: REPORT_BASICS_LINKS[ReportName.Insights],
        interpolation: {escapeValue: false},
      }),
      canCustomizeNumericBuckets: true,
      canUseMultiAttribution: canUseMultiAttributionVal && hasMultiAttributionProp,
      chartType: this.state.report.displayOptions.chartType,
      ...(dataGroupId ? {dataGroupId} : {}),
      dateRange: getDateRange(sections.time.clauses).getAttributes(),
      dateUnit: sections.time.clauses[0].unit as unknown as RollingWindowSize,
      disableAddEntryReason:
        this.state.report.displayOptions.chartType === ChartType.Table && this.state.report.hasTopEvents()
          ? `Events cannot be added on tables with "Your Top Events".`
          : ``,
      disableBreakdown: displayOptions.chartType === ChartType.FunnelTopPaths,
      enableEnhancedListFilters: true,
      enableHideEntry: true,
      entryRowLabelType: RowLabelType.Alpha,
      entryRowLabelTheme: RowLabelTheme.Gray,
      entriesDraggable: this.params.insightsReportMode === BookmarkType.Insights,
      entries,
      minEntries: 1,
      emptyPlaceholder:
        isNewMetricMenuEnabled && reportName === ReportName.Insights
          ? (INSIGHTS_DEFAULT_METRIC as QueryMultiMetricEntry)
          : ({...INSIGHTS_DEFAULT_EVENT, dataGroupId} as QuerySuperEntry),
      includeCustomProperties: IncludeCustomPropertyType.All,
      showBorrowedProperties: true,
      filtersOperator: getPropertyFiltersOperator(sections.filter.determiner || FilterSection.DETERMINER_ALL),
      filters: getPropertyFilters(sections.filter.clauses),
      formulas: getFormulas(sections.formula.clauses),
      formulasDisableReason,
      formulasError: this.params.error,
      groupBy,
      groupByUndraggableRows,
      hideDatePicker: true,
      metricsMode: true,
      otherFilterOptions: this.getOtherFilterOptions(multiAttributionProperty),
      otherGroupByOptions: this.getOtherGroupByOptions(canUseMultiAttributionVal, sections),
      otherAggregationOptions: this.getOtherAggregationOptions(),
      plotStyle: displayOptions.plotStyle,
      queryTypeOptions: [
        {
          value: MetricType.Retention,
          label: ({t}) => t(`retention.retention`),
          tooltipLabel: ({t}) =>
            `${t(`retention.retention-tooltip`)}\n[${t(`common.learn-more`)}](https://docs.mixpanel.com/docs/analysis/reports/retention#overview)`,
        },
        {
          value: MetricType.RetentionFrequency,
          label: ({t}) => t(`retention.frequency`),
          tooltipLabel: ({t}) =>
            `${t(`retention.frequency-tooltip`)}\n[${t(`common.learn-more`)}](https://docs.mixpanel.com/docs/analysis/reports/retention#frequency-view)`,
        },
      ],
      selectedQueryType:
        this.params.insightsReportMode === BookmarkType.Retention
          ? (sections.show.clauses[0] as BehaviorShowClause).behavior.type
          : null,
      rowTypes: getQueryModeRowTypes({
        canUseCohorts: this.canUseCohorts(),
        formulasDisableReason,
        enableNewMetricMenu: isNewMetricMenuEnabled,
      }),
      showEntryRename: true,
      showSpecialDatasetProperty: this.params.project.isRollupProject(),
      /**
       * Hide Top Events from events-menu if table chart has another metric already. Insights table UI for
       * column/metric labeling looks weird when Top Events is mixed with other events
       */
      restrictTopEventsOnly: this.state.report.displayOptions.chartType === ChartType.Table,
      supportB2bCohorts: this.canUseDataGroups(),
      switchableReportName: reportName,
      reportName,
      trackName: reportName,

      // disable profile history in profiles mode for now. need alignment on whether it's useful
      disableProfileHistory: this.state.report.sections.show.isPeopleOnlyQuery(),
      enableFilterAttribution: hasFunnelMetric(bookmarkParams),
      breakdownAttribution: [],
    };
  }

  onBookmarkUpdated(newBookmarkParams: InsightsBookmarkParams) {
    this.update({
      report: Report.fromBookmarkData(newBookmarkParams),
    });
  }

  private getOtherAggregationOptions(): Array<PropertySectionItem<string>> {
    const otherAggregationOptions: Array<PropertySectionItem<string>> = [
      ...(this.state.report.sections.show.hasEventsQuery() && this.canUsePageDuration() ? [PAGE_DURATION] : []),
    ];
    return otherAggregationOptions;
  }

  private getOtherFilterOptions(multiAttributionProperty: Segmentation | null): Array<PropertySectionItem<string>> {
    const otherFilterOptions: Array<PropertySectionItem<string>> = [
      ...(FeatureRollouts[FeatureRolloutName.Revenue].isEnabled(this.params.project)
        ? [RECURRING_REVENUE_CHANGE, RECURRING_REVENUE_CHANGE_TYPE]
        : []),
      ...(multiAttributionProperty
        ? [
            {
              ...segmentationToPropertyItem(multiAttributionProperty),
              ...BEHAVIOR_MULTI_ATTRIBUTION_FILTER,
              label: getSelectedLabelforBehavior(multiAttributionProperty.behavior),
            },
          ]
        : []),
      ...(this.canUseBehaviors() ? [BEHAVIOR_FREQUENCY, BEHAVIOR_AGGREGATION] : []),
      ...(this.state.report.sections.show.hasFunnelsQuery()
        ? [FUNNELS_TIME_TO_CONVERSION, {value: SPECIAL_EVENTS.SELECTED_STEP} as PropertySectionItem<string>]
        : []),
      ...(this.state.report.sections.show.hasEventsQuery() && this.canUsePageDuration() ? [PAGE_DURATION] : []),
    ];
    return otherFilterOptions;
  }

  private getOtherGroupByOptions(
    canUseMultiAttributionVal: boolean,
    sections: BuilderSections,
  ): Array<PropertySectionItem<string>> {
    return [
      ...(FeatureRollouts[FeatureRolloutName.Revenue].isEnabled(this.params.project)
        ? [RECURRING_REVENUE_CHANGE, RECURRING_REVENUE_CHANGE_TYPE]
        : []),
      ...(this.canUseBehaviors() ? [BEHAVIOR_FREQUENCY, BEHAVIOR_AGGREGATION] : []),
      ...(canUseMultiAttributionVal &&
      hasOnlyEventBasedMetrics({
        showClauses: sections.show.clauses,
        metricsResource: this.getContext(`metrics`),
        behaviorsResource: this.getContext(`behaviors`),
      })
        ? [BEHAVIOR_MULTI_ATTRIBUTION]
        : []),
      ...(this.state.report.sections.show.hasFunnelsQuery()
        ? [FUNNELS_TIME_TO_CONVERSION, {value: SPECIAL_EVENTS.SELECTED_STEP} as PropertySectionItem<string>]
        : []),
      ...(this.state.report.sections.show.hasEventsQuery() && this.canUsePageDuration() ? [PAGE_DURATION] : []),
    ];
  }

  canUseBehaviors() {
    return !this.state.report.sections.show.hasPeopleQuery();
  }

  canUsePageDuration() {
    return this.params.insightsReportMode === BookmarkType.Insights;
  }
}

export const InsightsQueryBuilderEl = defineAndMakeJsxFactory(`insights-query-builder`, InsightsQueryBuilder);
export const InsightsQueryBuilderReact = panel2react(`insights-query-builder`, InsightsQueryBuilder);
